import { PropsWithChildren, useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { IPaymentTemplate } from '@/entities/payment/lib/serialize';

import { VabsPaymentInfoRequestStatus } from '@/shared/api';
import {
  Button,
  DashboardDialog,
  Form,
  FormControl,
  FormField,
  FormItem,
  Typography,
} from '@/shared/ui';
import { Textarea } from '@/shared/ui/textarea';

import { ComplianceSchema, complianceSchema } from '../model/form';
import { PaymentBar } from './PaymentBar';
import { UploadInput } from './UploadInput';
import { toast } from 'sonner';
import { NotificationIcon } from '@/shared/assets';

interface ComplianceFormFile {
  id: string;
  file: File;
  customFileName: string;
}

export type ComplianceFormSubmit = (params: {
  data: ComplianceSchema;
  files: ComplianceFormFile[];
  requestId: string;
}) => Promise<void> | void;

interface ComplianceFormProps {
  payment: IPaymentTemplate;
  onFormSubmit: ComplianceFormSubmit;
  isSubmitting?: boolean;
  onClose: () => void;
  requestIdFallback?: string;
}

const splitRequestText = (text?: string | null): string[] => {
  if (!text) return [];

  const lines = text
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

  return lines.length ? lines : [text];
};

const ComplianceForm: React.FC<ComplianceFormProps> = ({
  payment,
  onFormSubmit,
  isSubmitting = false,
  onClose,
  requestIdFallback,
}) => {
  const form = useForm<ComplianceSchema>({
    resolver: zodResolver(complianceSchema),
    defaultValues: {
      comment: '' as string,
      fileIds: [],
    },
  });

  const [files, setFiles] = useState<ComplianceFormFile[]>([]);

  useEffect(() => {
    form.setValue(
      'fileIds',
      files.map(({ id }) => id),
      { shouldValidate: true },
    );
  }, [files, form]);

  const request = payment.infoRequests?.find(
    (request) =>
      request.status === VabsPaymentInfoRequestStatus.AwaitingClientResponse,
  )

  const requestId =
    request?.id ??
    requestIdFallback ??
    '';
  const allowedFormats = [
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
  ];
  const allowedFormatsLabel = allowedFormats.join(',');

  const handleAddFile = (file: File, customFileName: string) => {
    const lowerFileName = file.name.toLowerCase();
    const isAllowedFormat = allowedFormats.some((ext) =>
      lowerFileName.endsWith(ext),
    );
    if (!isAllowedFormat) {
      toast.error(`Unsupported file type. Allowed: ${allowedFormatsLabel}`);
      return;
    }
    setFiles((prev) => {
      if (prev.length >= 10) {
        toast.error('You can upload up to 10 documents');
        return prev;
      }
      const id = `${Date.now()}-${Math.random()}`;
      return [...prev, { id, file, customFileName }];
    });
  };

  const handleDeleteFile = (id: string) => {
    setFiles((prev) => prev.filter((item) => item.id !== id));
  };

  const onSubmit = async (data: ComplianceSchema) => {
    if (!requestId) return;
    await onFormSubmit({ data, files, requestId });
    onClose();
  };

  const handleSubmit = form.handleSubmit(onSubmit);

  const requestText = request?.requestText

  return (
    <div className="flex flex-col items-center">
      <PaymentBar payment={payment} />
      {!!requestText && <div className="w-full mt-1 flex items-center gap-1 rounded-[20px] bg-base-300 px-5 py-6">
        <NotificationIcon className="shrink-0" />
        <div className="flex flex-col items-start">
          {splitRequestText(requestText).map((text, index) => {
            return (
              <Typography variant="14-r" key={index}
                title={text}
                className="truncate">
                {text}
              </Typography>
            );
          })}
        </div>
      </div>}
      <Form {...form}>
        <form
          id="compliance-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-1 w-full"
        >
          <FormField
            control={form.control}
            name="comment"
            render={({ field, fieldState }) => (
              <FormItem className="w-full">
                <FormControl>
                  <Textarea
                    {...field}
                    className="mt-8 h-[120px] resize-none !rounded-[20px] py-[21px]"
                    variant="secondary"
                    placeholder="Add details"
                    error={fieldState.error?.message}
                  />
                </FormControl>
                {fieldState.error?.message && (
                  <Typography
                    variant="14-r"
                    className="mx-2 my-1 text-negative-500"
                  >
                    {fieldState.error?.message}
                  </Typography>
                )}
              </FormItem>
            )}
          />
          <UploadInput
            files={files}
            onAddFile={handleAddFile}
            onDeleteFile={handleDeleteFile}
          />
          <Button
            className="mb-1 mt-[40px] w-full"
            type="button"
            size="d-l"
            disabled={isSubmitting || !requestId}
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            Continue
          </Button>
        </form>
      </Form>
    </div>
  );
};

export const ComplianceModal = ({
  payment,
  children,
  onFormSubmit,
  title = 'We need some information',
  subtitle = 'Kindly clarify the detailed purpose of payment',
  isSubmitting = false,
  requestIdFallback,
  autoOpen,
  onAutoOpenDismiss,
}: PropsWithChildren<{
  payment: IPaymentTemplate;
  onFormSubmit: ComplianceFormSubmit;
  title?: string;
  subtitle?: string;
  isSubmitting?: boolean;
  requestIdFallback?: string;
  autoOpen?: boolean;
  onAutoOpenDismiss?: () => void;
}>) => {
  const [isOpen, setIsOpen] = useState(false);
  const shouldAutoOpen = autoOpen ?? false;

  useEffect(() => {
    if (shouldAutoOpen) {
      setIsOpen(true);
    }
  }, [shouldAutoOpen]);

  const handleClose = () => {
    setIsOpen(false);
    onAutoOpenDismiss?.();
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      onAutoOpenDismiss?.();
    }
  };
  return (
    <DashboardDialog
      triggerProps={{
        asChild: true,
        onClick: () => {
          setIsOpen(true);
        },
      }}
      modalProps={{
        classNames: {
          title: 'heading-s',
          description: 'typography-14r pt-2 max-w-[210px] mx-auto'
        },
      }}
      content={
        <ComplianceForm
          payment={payment}
          onFormSubmit={onFormSubmit}
          isSubmitting={isSubmitting}
          requestIdFallback={requestIdFallback}
          onClose={handleClose}
        />
      }
      animationProps={{ isFirst: true }}
      headerProps={{ className: 'h-9 min-h-0' }}
      title={title}
      subtitle={subtitle}
      open={isOpen}
      onOpenChange={handleOpenChange}
      onClose={handleClose}
    >
      {children}
    </DashboardDialog>
  );
};
